import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Typography, Box, CircularProgress, Alert, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Chip, InputAdornment, Stack, Divider, Paper, Grid } from "@mui/material";
import { API_ENDPOINTS } from "../config/api.config";
import { ENABLE_TAB_MAPPING, ENABLE_TAB_CONTRACT } from "../config/local.config";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PeopleIcon from '@mui/icons-material/People';
import HelpIcon from '@mui/icons-material/Help';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { parseLocalDate } from '../utils/dateHelpers';

export default function Projects() {
  const navigate = useNavigate();
  const { canManageProjects, canDeleteProjects, isAdmin, isServiceManager, isTeamLeader, isJRServiceManager } = useRoleAccess();
  const { userId } = useCurrentUser();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  // Stato per i customers (per il dropdown)
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  // Yubiq Code disponibili (dai Contracts) per il customer selezionato nel dialog
  const [yubiqOptions, setYubiqOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);

  // Stato per il dialog di create/update
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    customer_id: '',
    start_date: '',
    end_date: '',
    active: true,
    project_code: '',
    days_after_block: 1,
    days_before_block: 1,
    ticket_id_prefix: '',
    regex: '',
    regex_desc: '',
    previous_month_blocked: false,
    yubiq_code: '',
    tipologia_servizio: '',
  });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regexHelpOpen, setRegexHelpOpen] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    projectId: null,
    projectName: '',
  });
  const [deleteErrorDialog, setDeleteErrorDialog] = useState({
    open: false,
    message: '',
  });
  const [deletingProject, setDeletingProject] = useState(false);
  
  // Progetti non eliminabili (worklog o report mapping associati)
  const [projectsWithWorklogs, setProjectsWithWorklogs] = useState(new Set());
  const [projectsWithReportMappings, setProjectsWithReportMappings] = useState(new Set());

  const getProjectDeleteBlockerTooltip = (projectId) => {
    const hints = [];
    if (projectsWithWorklogs.has(projectId)) {
      hints.push('remove or reassign all worklogs');
    }
    if (ENABLE_TAB_MAPPING && projectsWithReportMappings.has(projectId)) {
      hints.push('remove all report mappings from the Report Mapping tab');
    }
    if (hints.length === 0) return null;
    return `Cannot delete project. Before deleting, ${hints.join('; ')}.`;
  };

  const isProjectDeleteBlocked = (projectId) =>
    projectsWithWorklogs.has(projectId)
    || (ENABLE_TAB_MAPPING && projectsWithReportMappings.has(projectId));

  // Definizione delle colonne per la DataGrid
  const columns = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {/* <Tooltip title="Manage Project">
            <IconButton size="small" onClick={() => navigate(`/projects/${params.row.id}`)}>
              <ManageAccountsIcon />
            </IconButton>
          </Tooltip> */}

          {canManageProjects() && (
            <Tooltip title="Edit Project">
              <IconButton size="small" onClick={() => navigate(`/projects/${params.row.id}`)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {canDeleteProjects() && (
            isProjectDeleteBlocked(params.row.id) ? (
              <Tooltip title={getProjectDeleteBlockerTooltip(params.row.id)}>
                <span>
                  <IconButton size="small" disabled sx={{ color: 'grey.400' }}>
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => openDeleteConfirmDialog(params.row)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )
          )}
        </Box>
      ),
    },
    { 
      field: 'active', 
      headerName: 'Status', 
      width: 70,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: params.value ? '#4caf50' : '#bdbdbd',
              flexShrink: 0,
            }}
            title={params.value ? 'Active' : 'Inactive'}
          />
        </Box>
      )
    },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'project_code', headerName: 'Code', width: 120 },
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'ticket_id_prefix', headerName: 'Ticket ID Prefix', width: 150 },
    { field: 'regex', headerName: 'Regex Pattern', width: 200 },
    { field: 'regex_desc', headerName: 'Regex Description', width: 200 },
    { 
      field: 'customer_name', 
      headerName: 'Customer', 
      width: 150,
      valueGetter: (value, row) => {
        return row.customer?.name || 'N/A';
      }
    },
    { 
      field: 'worklog_restrictions', 
      headerName: 'Worklog Restrictions', 
      width: 250,
      sortable: false,
      renderCell: (params) => {
        // Debug: log dei valori per verificare cosa arriva dal backend
        console.log('Worklog Restrictions Debug:', {
          project_id: params.row.id,
          project_name: params.row.name,
          days_before_block: params.row.days_before_block,
          days_after_block: params.row.days_after_block,
          previous_month_blocked: params.row.previous_month_blocked
        });
        
        const chipSx = { fontSize: '0.65rem', height: '18px', width: 'fit-content' };

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
            {/* Prima riga: days before e after */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
              <Chip
                label={`${params.row.days_before_block || 1}d before`}
                size="small"
                variant="outlined"
                sx={chipSx}
              />
              <Chip
                label={`${params.row.days_after_block || 1}d after`}
                size="small"
                variant="outlined"
                sx={chipSx}
              />
            </Box>
            {/* Seconda riga: previous month (sempre visibile) */}
            <Chip
              label={`Prev month: ${params.row.previous_month_blocked ? 'blocked' : 'allowed'}`}
              size="small"
              variant="outlined"
              color={params.row.previous_month_blocked ? 'warning' : 'success'}
              sx={{
                ...chipSx,
                borderWidth: '1px',
                color: 'black',
                '& .MuiChip-label': {
                  color: 'black',
                },
              }}
            />
          </Box>
        );
      }
    },
    { 
      field: 'start_date', 
      headerName: 'Start Date', 
      width: 120,
      valueFormatter: (value) => {
        if (!value) return '';
        // Fix timezone: parseLocalDate interpreta la data come locale
        const date = parseLocalDate(value);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-GB');
      }
    },
    { 
      field: 'end_date', 
      headerName: 'End Date', 
      width: 120,
      valueFormatter: (value) => {
        if (!value) return '';
        // Fix timezone: parseLocalDate interpreta la data come locale
        const date = parseLocalDate(value);
        if (isNaN(date.getTime())) return '';
        // Gestisce date "infinite" (anno 9999)
        if (date.getFullYear() >= 9999) return 'No End Date';
        return date.toLocaleDateString('en-GB');
      }
    },
    { field: 'description', headerName: 'Description', width: 250 },
    { 
      field: 'created_at', 
      headerName: 'Created At', 
      width: 140,
      valueFormatter: (value) => {
        if (!value) return '';
        // Fix timezone: parseLocalDate interpreta datetime come locale
        const date = parseLocalDate(value);
        return date.toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      }
    },
    { 
      field: 'updated_at', 
      headerName: 'Updated At', 
      width: 140,
      valueFormatter: (value) => {
        if (!value) return '';
        // Fix timezone: parseLocalDate interpreta datetime come locale
        const date = parseLocalDate(value);
        return date.toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      }
    },
  ];

  // Fetch projects
  const fetchProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      let projects = [];
      
      if (isAdmin) {
        // Admin vede tutti i progetti
        console.log('[PROJECTS] Admin user - fetching all projects');
        const response = await fetch(API_ENDPOINTS.PROJECTS);
        console.log('[PROJECTS] Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.log('[PROJECTS] Error response:', errorText);
          throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
        }
        
        const data = await response.json();
        console.log('[PROJECTS] Response data:', data);
        projects = data.projects || [];
      } else if (isServiceManager || isTeamLeader || isJRServiceManager) {
        // Service Manager, Team Leader, JR Service Manager vedono solo progetti con ruoli 1,2,3
        console.log('[PROJECTS] Fetching project users for userId:', userId);
        const response = await fetch(`${API_ENDPOINTS.PROJECT_USERS}?user_id=${userId}`);
        console.log('[PROJECTS] Project users API response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.log('[PROJECTS] Error response:', errorText);
          throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
        }
        
        const data = await response.json();
        console.log('[PROJECTS] Project users API response:', data);
        
        if (Array.isArray(data.user_projects_with_references)) {
          const currentDate = new Date();
          
          // Filtra per project_role_id in [1,2,3] e validity_end_date
          const validProjectUsers = data.user_projects_with_references.filter(item => {
            const projectRoleId = item.project_role?.id;
            const isValidRole = [1, 2, 3].includes(projectRoleId);
            
            // Se validity_end_date è vuota, è sempre valida per il filtro progetti
            if (!item.validity_end_date) {
              console.log('[PROJECTS] User without validity_end_date (always valid):', item);
              return isValidRole;
            }
            
            // Fix timezone: parseLocalDate interpreta la data come locale
            const endDate = parseLocalDate(item.validity_end_date);
            const isValidDate = endDate > currentDate;
            
            console.log('[PROJECTS] Filter check for project:', {
              project_id: item.project?.id,
              project_name: item.project?.name,
              validity_end_date: item.validity_end_date,
              endDate,
              currentDate,
              isValidDate,
              project_role_id: projectRoleId,
              isValidRole,
              final: isValidDate && isValidRole
            });
            
            return isValidDate && isValidRole;
          });
          console.log('[PROJECTS] Valid project users after filter:', validProjectUsers);
          
          // Estrai i progetti direttamente dalla risposta
          projects = validProjectUsers.map(item => ({
            id: item.project.id,
            name: item.project.name,
            description: item.project.description,
            active: item.project.active,
            project_code: item.project.project_code,
            customer_id: item.project.customer_id,
            start_date: item.project.start_date,
            end_date: item.project.end_date,
            days_after_block: item.project.days_after_block,
            days_before_block: item.project.days_before_block,
            ticket_id_prefix: item.project.ticket_id_prefix,
            regex: item.project.regex,
            regex_desc: item.project.regex_desc,
            previous_month_blocked: item.project.previous_month_blocked,
            customer: item.customer
          }));
          
          console.log('[PROJECTS] Final projects set:', projects);
        }
      } else {
        // Altri ruoli usano la logica originale
        console.log('[PROJECTS] Other roles - fetching from:', API_ENDPOINTS.PROJECTS);
        const response = await fetch(API_ENDPOINTS.PROJECTS);
        console.log('[PROJECTS] Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.log('[PROJECTS] Error response:', errorText);
          throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
        }
        
        const data = await response.json();
        console.log('[PROJECTS] Response data:', data);
        projects = data.projects || [];
      }
      
      console.log('[PROJECTS] Projects loaded:', projects.length, 'items');
      const projectRows = projects.map((row, idx) => ({ id: row.id || idx, ...row }));
      setRows(projectRows);
      checkProjectsDeleteBlockers(projectRows);
    } catch (err) {
      console.error('[PROJECTS] Fetch error:', err);
      setError(`Error loading projects: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch customers for dropdown
  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.CUSTOMERS);
      if (!response.ok) throw new Error("Error fetching customers");
      const data = await response.json();
      const customersList = data.customers || [];
      console.log('Customers loaded for dropdown:', customersList);
      setCustomers(customersList);
    } catch (err) {
      console.error('Error loading customers:', err);
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
    }
  };

  const fetchTypeOptions = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=contract_type`);
      if (!response.ok) return;
      const data = await response.json();
      setTypeOptions(data.items || []);
    } catch {
      setTypeOptions([]);
    }
  };

  const checkProjectsDeleteBlockers = async (projects = []) => {
    try {
      const projectIds = projects.map((project) => project.id).join(',');
      const [worklogsResponse, mappingResponse] = await Promise.all([
        fetch(API_ENDPOINTS.WORKLOGS),
        ENABLE_TAB_MAPPING && projects.length > 0
          ? fetch(`${API_ENDPOINTS.SHEET_MAPPING}?project_ids=${projectIds}`)
          : Promise.resolve(null),
      ]);

      if (worklogsResponse.ok) {
        const data = await worklogsResponse.json();
        const worklogs = data.worklogs || [];
        setProjectsWithWorklogs(new Set(
          worklogs.map((worklog) => worklog.project_id).filter((projectId) => projectId)
        ));
      } else {
        setProjectsWithWorklogs(new Set());
      }

      if (mappingResponse?.ok) {
        const payload = await mappingResponse.json();
        const blockedProjectIds = payload.project_ids || [];
        setProjectsWithReportMappings(new Set(blockedProjectIds));
      } else {
        setProjectsWithReportMappings(new Set());
      }
    } catch (err) {
      console.error('Error checking project delete blockers:', err);
      setProjectsWithWorklogs(new Set());
      setProjectsWithReportMappings(new Set());
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchCustomers();
    fetchTypeOptions();
  }, [isAdmin, isServiceManager, isTeamLeader, isJRServiceManager, userId]);

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) {
      return rows;
    }

    const query = searchQuery.toLowerCase().trim();

    return rows.filter((row) => {
      const searchableValues = [
        row.id,
        row.name,
        row.project_code,
        row.description,
        row.customer?.name,
        row.ticket_id_prefix,
        row.regex,
        row.regex_desc,
        row.active ? 'active' : 'inactive',
      ];

      return searchableValues.some((value) =>
        value != null && String(value).toLowerCase().includes(query)
      );
    });
  }, [rows, searchQuery]);

  const parseApiErrorMessage = async (response) => {
    const text = await response.text();
    if (!text) {
      return `Request failed (${response.status})`;
    }

    try {
      const data = JSON.parse(text);
      return data.error || data.message || text;
    } catch {
      return text;
    }
  };

  const openDeleteConfirmDialog = (project) => {
    setDeleteConfirmDialog({
      open: true,
      projectId: project.id,
      projectName: project.name || `Project #${project.id}`,
    });
  };

  const closeDeleteConfirmDialog = () => {
    if (deletingProject) return;
    setDeleteConfirmDialog({ open: false, projectId: null, projectName: '' });
  };

  const closeDeleteErrorDialog = () => {
    setDeleteErrorDialog({ open: false, message: '' });
  };

  const handleConfirmDeleteProject = async () => {
    const { projectId } = deleteConfirmDialog;
    if (!projectId) return;

    setDeletingProject(true);

    try {
      const response = await fetch(`${API_ENDPOINTS.PROJECTS}?id=${projectId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response));
      }

      setDeleteConfirmDialog({ open: false, projectId: null, projectName: '' });
      fetchProjects();
    } catch (err) {
      setDeleteConfirmDialog({ open: false, projectId: null, projectName: '' });
      setDeleteErrorDialog({
        open: true,
        message: err.message || 'Unable to delete project. Please try again.',
      });
    } finally {
      setDeletingProject(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      customer_id: '',
      start_date: '',
      end_date: '',
      active: true,
      project_code: '',
      days_after_block: 1,
      days_before_block: 1,
      ticket_id_prefix: '',
      regex: '',
      regex_desc: '',
      previous_month_blocked: false,
      yubiq_code: '',
      tipologia_servizio: '',
    });
    setEditingRow(null);
    setFormError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormError(null);
  };

  const handleEditDialog = (row) => {
    // Gestione delle date con controllo per date "infinite"
    const getFormattedDate = (dateStr) => {
      if (!dateStr) return '';
      // Se la data è già in formato YYYY-MM-DD, verifica direttamente l'anno
      if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const year = parseInt(dateStr.split('-')[0], 10);
        // Se è una data "infinita" (anno 9999), non la mostriamo nel form
        if (year >= 9999) return '';
        return dateStr;
      }
      // Se la data contiene 'T' (formato ISO con orario), estrai solo la parte data
      if (typeof dateStr === 'string' && dateStr.includes('T')) {
        const datePart = dateStr.split('T')[0];
        const year = parseInt(datePart.split('-')[0], 10);
        if (year >= 9999) return '';
        return datePart;
      }
      // Fallback: converti tramite Date object (fix timezone)
      const date = parseLocalDate(dateStr);
      if (isNaN(date.getTime())) return '';
      // Se è una data "infinita" (anno 9999), non la mostriamo nel form
      if (date.getFullYear() >= 9999) return '';
      // Usa le componenti locali per evitare shift di timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setFormData({
      id: row.id || '',
      name: row.name || '',
      description: row.description || '',
      customer_id: row.customer?.id || '',
      start_date: getFormattedDate(row.start_date),
      end_date: getFormattedDate(row.end_date),
      active: row.active !== undefined ? row.active : true,
      project_code: row.project_code || '',
      days_after_block: row.days_after_block || 1,
      days_before_block: row.days_before_block || 1,
      ticket_id_prefix: row.ticket_id_prefix || '',
      regex: row.regex || '',
      regex_desc: row.regex_desc || '',
      previous_month_blocked: row.previous_month_blocked || false,
      yubiq_code: row.yubiq_code || '',
      tipologia_servizio: row.tipologia_servizio || '',
    });
    setEditingRow(row);
    setFormError(null);
    setOpenDialog(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      // Cambiando customer, resetta lo Yubiq Code (appartiene ad un altro customer)
      ...(name === 'customer_id' ? { yubiq_code: '' } : {}),
    }));
  };

  // Carica gli Yubiq Code disponibili (dai Contracts) per il customer selezionato nel dialog
  useEffect(() => {
    if (!openDialog || !formData.customer_id) {
      setYubiqOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_ENDPOINTS.CONTRACTS}?customer_id=${formData.customer_id}`);
        if (!res.ok) return;
        const data = await res.json();
        const codes = (data.contracts || [])
          .map((c) => c.yubiq_code)
          .filter((code) => code != null && code !== '');
        if (!cancelled) setYubiqOptions(Array.from(new Set(codes)));
      } catch {
        if (!cancelled) setYubiqOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [openDialog, formData.customer_id]);

  const handleRegexHelpOpen = () => {
    setRegexHelpOpen(true);
  };

  const handleRegexHelpClose = () => {
    setRegexHelpOpen(false);
  };

  // Salva project (create/update)
  // NOTA: Al momento l'API supporta solo CREATE. UPDATE restituisce 403 Forbidden
  const handleSaveProject = async () => {
    setSaving(true);
    setFormError(null);
    
    if (!formData.name.trim()) {
      setFormError('Name is required.');
      setSaving(false);
      return;
    }

    if (!formData.customer_id) {
      setFormError('Customer is required.');
      setSaving(false);
      return;
    }

    if (!formData.project_code.trim()) {
      setFormError('Project code is required.');
      setSaving(false);
      return;
    }

    if (!String(formData.start_date || '').trim()) {
      setFormError('Start date is required.');
      setSaving(false);
      return;
    }

    if (!String(formData.end_date || '').trim()) {
      setFormError('End date is required.');
      setSaving(false);
      return;
    }

    if (formData.start_date && formData.end_date && formData.end_date < formData.start_date) {
      setFormError('End date must be on or after start date.');
      setSaving(false);
      return;
    }

    if (!formData.days_after_block || formData.days_after_block < 1) {
      setFormError('Days after block is required and must be greater than 0.');
      setSaving(false);
      return;
    }

    if (!formData.days_before_block || formData.days_before_block < 1) {
      setFormError('Days before block is required and must be greater than 0.');
      setSaving(false);
      return;
    }

    try {
      let payload, url, method;
      
      if (editingRow) {
        // Update - usa PUT come specificato nell'API
        payload = {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          customer_id: parseInt(formData.customer_id),
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          active: formData.active,
          project_code: formData.project_code.trim(),
          days_after_block: parseInt(formData.days_after_block),
          days_before_block: parseInt(formData.days_before_block),
          ticket_id_prefix: formData.ticket_id_prefix.trim() || null,
          regex: formData.regex.trim() || null,
          regex_desc: formData.regex_desc.trim() || null,
          previous_month_blocked: formData.previous_month_blocked,
          yubiq_code: formData.yubiq_code || null,
          tipologia_servizio: formData.tipologia_servizio || null,
          ...(userId ? { user_id: userId } : {}),
        };
        url = `${API_ENDPOINTS.PROJECTS}?id=${editingRow.id}`;
        method = 'PUT';
      } else {
        // Create - non includere l'ID
        payload = {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          customer_id: parseInt(formData.customer_id),
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          active: formData.active,
          project_code: formData.project_code.trim(),
          days_after_block: parseInt(formData.days_after_block),
          days_before_block: parseInt(formData.days_before_block),
          ticket_id_prefix: formData.ticket_id_prefix.trim() || null,
          regex: formData.regex.trim() || null,
          regex_desc: formData.regex_desc.trim() || null,
          previous_month_blocked: formData.previous_month_blocked,
          yubiq_code: formData.yubiq_code || null,
          tipologia_servizio: formData.tipologia_servizio || null,
          ...(userId ? { user_id: userId } : {}),
        };
        url = API_ENDPOINTS.PROJECTS;
        method = 'POST';
      }

      console.log(`[PROJECTS] ${method} request to:`, url);
      console.log('[PROJECTS] Payload:', payload);

      const response = await fetch(url, {
        method: method,
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('[PROJECTS] Response status:', response.status);
      
      if (!response.ok) {
        const err = await response.text();
        console.log('[PROJECTS] Error response:', err);
        
        // Se è un errore CORS, prova con approach alternativo
        if (err.includes('CORS') || response.status === 0) {
          console.log('[PROJECTS] Detected CORS issue, trying alternative approach...');
          throw new Error('CORS error detected. Please check server configuration.');
        }
        
        throw new Error(`${response.status}: ${err || 'Request failed'}`);
      }

      const result = await response.json();
      console.log('[PROJECTS] Update successful:', result);

      // Se è una creazione (non editing), aggiungi automaticamente l'utente come Service Manager
      if (!editingRow) {
        console.log('[PROJECTS] Create successful, adding user as Service Manager');
        
        try {
          // Estrai l'ID del progetto dalla risposta
          const projectId = result.id || result.project?.id;
          if (projectId) {
            console.log('[PROJECTS] Adding user as Service Manager for project ID:', projectId);
            
            // Crea l'associazione utente-progetto con ruolo Service Manager (project_role_id = 1)
            const userProjectPayload = {
              user_id: userId,
              project_role_id: 1 // Service Manager
            };
            
            const userProjectResponse = await fetch(`${API_ENDPOINTS.PROJECT_USERS}?project_id=${projectId}`, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify(userProjectPayload)
            });
            
            if (userProjectResponse.ok) {
              console.log('[PROJECTS] User successfully added as Service Manager');
            } else {
              const errorText = await userProjectResponse.text();
              console.error('[PROJECTS] Error adding user as Service Manager:', errorText);
              // Non bloccare il processo se l'aggiunta dell'utente fallisce
            }
          } else {
            console.error('[PROJECTS] Could not extract project ID from response:', result);
          }
        } catch (userProjectError) {
          console.error('[PROJECTS] Error adding user as Service Manager:', userProjectError);
          // Non bloccare il processo se l'aggiunta dell'utente fallisce
        }
      } else {
        console.log('[PROJECTS] Update successful for project ID:', editingRow.id);
      }

      setOpenDialog(false);
      setEditingRow(null);
      
      // Ricarica la lista
      fetchProjects();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>
            Projects Management
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            {canManageProjects() && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                color="primary"
                onClick={handleOpenDialog}
              >
                Add Project
              </Button>
            )}
          </Box>
        </Box>
        
        {/* Barra di ricerca */}
        <Box mb={2}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by project name, code, customer, ticket prefix, regex, status, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ 
              bgcolor: 'white',
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              }
            }}
          />
          {searchQuery && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Found {filteredRows.length} project{filteredRows.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </Typography>
          )}
        </Box>
        
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {error}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This is likely a backend API issue (HTTP 500). Please check the browser console for more details.
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={fetchProjects}
                sx={{ mr: 1 }}
              >
                🔄 Retry
              </Button>
              <Button 
                variant="text" 
                size="small" 
                onClick={() => console.log('API Endpoint:', API_ENDPOINTS.PROJECTS)}
              >
                Show Endpoint in Console
              </Button>
            </Box>
          </Alert>
        ) : (
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              initialState={{
                columns: {
                  columnVisibilityModel: { id: false },
                },
              }}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              pagination
              hideFooter={false}
              sx={{ bgcolor: 'white' }}
              disableRowSelectionOnClick
            />
          </Box>
        )}
      </Box>

      {/* Dialog per create/update project */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth 
          scroll="body"
          sx={{
            '& .MuiDialog-container': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              paddingLeft: '16px',
              paddingRight: '16px',
              paddingBottom: 'max(28px, env(safe-area-inset-bottom, 0px))',
              paddingTop: 'max(72px, calc(env(safe-area-inset-top, 0px) + 48px))',
            },
          }}
          PaperProps={{
            sx: {
              margin: '0 auto',
              maxHeight: 'min(900px, calc(100vh - 144px))',
              width: '100%',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
        <DialogTitle
          sx={{
            py: 2,
            px: 2.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {editingRow ? (
                  <EditNoteOutlinedIcon fontSize="small" />
                ) : (
                  <FolderOpenOutlinedIcon fontSize="small" />
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {editingRow ? 'Edit project' : 'Add project'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {editingRow
                    ? 'Update settings for this project. Changes apply to worklog rules and ticket validation.'
                    : 'Create a new project and link it to a customer. You can refine teams and users after saving.'}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={editingRow ? 'Editing' : 'New'}
                    color={editingRow ? 'default' : 'primary'}
                    variant={editingRow ? 'outlined' : 'filled'}
                    sx={{ fontWeight: 500 }}
                  />
                  {formData.active ? (
                    <Chip size="small" label="Active" color="success" variant="outlined" />
                  ) : (
                    <Chip size="small" label="Inactive" variant="outlined" />
                  )}
                </Stack>
              </Box>
            </Stack>
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              size="small"
              sx={{ color: 'text.secondary', mt: -0.5 }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5, bgcolor: 'grey.50', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Stack spacing={2.5}>
            {formError && (
              <Alert severity="error" onClose={() => setFormError(null)} sx={{ borderRadius: 1.5 }}>
                {formError}
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <BusinessOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                  Core details
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="name"
                    label="Project name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    fullWidth
                    variant="outlined"
                    error={!formData.name.trim() && !!formError}
                    helperText={!formData.name.trim() && formError ? 'Name is required' : undefined}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="project_code"
                    label="Project code"
                    value={formData.project_code}
                    onChange={handleFormChange}
                    required
                    fullWidth
                    variant="outlined"
                    error={!formData.project_code.trim() && !!formError}
                    helperText={!formData.project_code.trim() && formError ? 'Project code is required' : undefined}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required error={!formData.customer_id && !!formError}>
                    <InputLabel>Customer</InputLabel>
                    <Select
                      name="customer_id"
                      value={formData.customer_id}
                      onChange={handleFormChange}
                      label="Customer"
                      disabled={customersLoading}
                    >
                      {customersLoading ? (
                        <MenuItem value="">
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CircularProgress size={20} />
                            <span>Loading customers…</span>
                          </Stack>
                        </MenuItem>
                      ) : (
                        customers.map((customer) => (
                          <MenuItem key={customer.id} value={customer.id}>
                            {customer.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                {ENABLE_TAB_CONTRACT && (<>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Service Type</InputLabel>
                    <Select
                      name="tipologia_servizio"
                      value={formData.tipologia_servizio}
                      onChange={handleFormChange}
                      label="Service Type"
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {formData.tipologia_servizio && !typeOptions.some((o) => o.value === formData.tipologia_servizio) && (
                        <MenuItem value={formData.tipologia_servizio}>
                          {formData.tipologia_servizio} (inactive)
                        </MenuItem>
                      )}
                      {typeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label || opt.value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={!formData.customer_id}>
                    <InputLabel>Yubiq Code</InputLabel>
                    <Select
                      name="yubiq_code"
                      value={formData.yubiq_code}
                      onChange={handleFormChange}
                      label="Yubiq Code"
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {formData.yubiq_code && !yubiqOptions.includes(formData.yubiq_code) && (
                        <MenuItem value={formData.yubiq_code}>{formData.yubiq_code}</MenuItem>
                      )}
                      {yubiqOptions.map((code) => (
                        <MenuItem key={code} value={code}>{code}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                </>)}
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <ConfirmationNumberOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                  Ticket ID and validation
                </Typography>
              </Stack>
              <Stack spacing={2}>
                <TextField
                  name="ticket_id_prefix"
                  label="Ticket ID prefix"
                  value={formData.ticket_id_prefix}
                  onChange={handleFormChange}
                  fullWidth
                  variant="outlined"
                  placeholder="e.g. SAAAMSABH-, TICKET-"
                  helperText="Optional. When set, this prefix is prepended for ticket resolution activities."
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                  <TextField
                    name="regex"
                    label="Regex pattern"
                    value={formData.regex}
                    onChange={handleFormChange}
                    fullWidth
                    variant="outlined"
                    placeholder="e.g. ^[A-Z]+-\d+$"
                    helperText="Optional. Validates the part after the prefix, if a prefix is set."
                  />
                  <Tooltip title="Regex help and examples">
                    <IconButton
                      onClick={handleRegexHelpOpen}
                      color="primary"
                      sx={{
                        mt: { xs: 0, sm: 0.5 },
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        flexShrink: 0,
                      }}
                    >
                      <HelpIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <TextField
                  name="regex_desc"
                  label="Regex description (shown to users)"
                  value={formData.regex_desc}
                  onChange={handleFormChange}
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={2}
                  placeholder="Short hint for users when entering a ticket ID in worklogs"
                  helperText="Shown in worklog forms to explain the expected ticket format."
                />
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <CalendarMonthOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                  Schedule
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="start_date"
                    label="Start date *"
                    type="date"
                    value={formData.start_date}
                    onChange={handleFormChange}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    error={!String(formData.start_date || '').trim() && !!formError}
                    helperText={
                      !String(formData.start_date || '').trim() && formError
                        ? 'Start date is required'
                        : undefined
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="end_date"
                    label="End date *"
                    type="date"
                    value={formData.end_date}
                    onChange={handleFormChange}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    error={
                      (!String(formData.end_date || '').trim() && !!formError) ||
                      !!(
                        formData.start_date &&
                        formData.end_date &&
                        formData.end_date < formData.start_date
                      )
                    }
                    helperText={
                      formData.start_date &&
                      formData.end_date &&
                      formData.end_date < formData.start_date
                        ? 'Must be on or after start date'
                        : !String(formData.end_date || '').trim() && formError
                          ? 'End date is required'
                          : undefined
                    }
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <TuneOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                  Worklog time window
                </Typography>
              </Stack>
              <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2, borderRadius: 1.5 }}>
                Control how many days before and after today users can add or edit worklogs for this project.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="days_before_block"
                    label="Days before (allowed)"
                    type="number"
                    value={formData.days_before_block}
                    onChange={handleFormChange}
                    required
                    fullWidth
                    variant="outlined"
                    inputProps={{ min: 1 }}
                    error={!formData.days_before_block || formData.days_before_block < 1}
                    helperText={
                      !formData.days_before_block || formData.days_before_block < 1
                        ? 'Must be greater than 0'
                        : 'Past window from today'
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="days_after_block"
                    label="Days after (allowed)"
                    type="number"
                    value={formData.days_after_block}
                    onChange={handleFormChange}
                    required
                    fullWidth
                    variant="outlined"
                    inputProps={{ min: 1 }}
                    error={!formData.days_after_block || formData.days_after_block < 1}
                    helperText={
                      !formData.days_after_block || formData.days_after_block < 1
                        ? 'Must be greater than 0'
                        : 'Future window from today'
                    }
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    name="previous_month_blocked"
                    checked={formData.previous_month_blocked}
                    onChange={handleFormChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                      Block previous month
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      When enabled, users cannot create or change worklogs for the previous calendar month.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02, mb: 2 }}>
                Additional
              </Typography>
              <Stack spacing={2}>
                <TextField
                  name="description"
                  label="Description"
                  value={formData.description}
                  onChange={handleFormChange}
                  multiline
                  minRows={3}
                  fullWidth
                  variant="outlined"
                  placeholder="Internal notes or scope summary (optional)"
                />
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="active"
                        checked={formData.active}
                        onChange={handleFormChange}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Active project
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Inactive projects are hidden from most operational flows.
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                </Paper>
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            gap: 1,
            justifyContent: 'flex-end',
          }}
        >
          <Button onClick={handleCloseDialog} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveProject}
            variant="contained"
            color="primary"
            disabled={saving}
            sx={{ minWidth: 140 }}
          >
            {saving ? (
              <CircularProgress size={22} color="inherit" />
            ) : editingRow ? (
              'Save changes'
            ) : (
              'Create project'
            )}
          </Button>
        </DialogActions>
        </Dialog>

        {/* Regex Help Dialog */}
        <Dialog 
          open={regexHelpOpen} 
          onClose={handleRegexHelpClose}
          maxWidth="md"
          fullWidth
          scroll="body"
          sx={{
            '& .MuiDialog-container': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              paddingLeft: '16px',
              paddingRight: '16px',
              paddingBottom: 'max(28px, env(safe-area-inset-bottom, 0px))',
              paddingTop: 'max(72px, calc(env(safe-area-inset-top, 0px) + 48px))',
            },
          }}
          PaperProps={{
            sx: {
              margin: '0 auto',
              maxHeight: 'min(900px, calc(100vh - 144px))',
              width: '100%',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HelpIcon color="primary" />
              <Typography variant="h6">Regex Pattern Help</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              How to fill in the Ticket ID regex field
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              A <strong>regex</strong> helps check that your Ticket IDs follow the correct pattern. If you don’t know how to write one, just use one of the examples below or change the letters/numbers to fit your needs.
            </Typography>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Ready-to-use examples:
            </Typography>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 2 }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0', py: 1 }}>Ticket Format</Box>
                  <Box component="th" sx={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0', py: 1 }}>Use this regex</Box>
                </Box>
              </Box>
              <Box component="tbody">
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>ABC-123 (Jira)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^[A-Z]+-\d+$</code></Box>
                </Box>
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>12345 (Numbers only)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^\d+$</code></Box>
                </Box>
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>INC0012345 (ServiceNow)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^INC\d+$</code></Box>
                </Box>
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>REQ45A (Letters+Numbers)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^[A-Za-z0-9]+$</code></Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              How to set exact or maximum length limits
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <li>
                <Typography variant="body2"><strong>To require exactly 5 numbers</strong>: Use <code>^\d{'{'}5{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>12345</code>. Not valid: <code>1234</code>, <code>123456</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>To require between 3 and 6 letters</strong>: Use <code>^[A-Za-z]{'{'}3,6{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>ABC</code>, <code>abcd</code>, <code>AbCdEf</code>. Not valid: <code>AB</code>, <code>ABCDEFG</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>To require at most 4 digits</strong>: Use <code>^\d{'{'}1,4{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>1</code>, <code>1234</code>. Not valid: <code>12345</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>To require at least 3 digits</strong>: Use <code>^\d{'{'}3,{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>123</code>, <code>1234567</code></Typography>
              </li>
            </Box>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              What do the main symbols mean?
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <li><Typography variant="body2"><code>^</code> → means “must start with”</Typography></li>
              <li><Typography variant="body2"><code>$</code> → means “must end with”</Typography></li>
              <li><Typography variant="body2"><code>[A-Z]</code> → one UPPERCASE letter</Typography></li>
              <li><Typography variant="body2"><code>[a-z]</code> → one lowercase letter</Typography></li>
              <li><Typography variant="body2"><code>\d</code> → a digit (0 to 9)</Typography></li>
              <li><Typography variant="body2"><code>+</code> → one or more of the previous character</Typography></li>
              <li><Typography variant="body2"><code>{'{'}n{'}'}</code> → exactly n characters</Typography></li>
              <li><Typography variant="body2"><code>{'{'}n,m{'}'}</code> → between n and m characters (inclusive)</Typography></li>
              <li><Typography variant="body2"><code>{'{'}n,{'}'}</code> → at least n characters</Typography></li>
            </Box>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Practical examples
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 1 }}>
              <li>
                <Typography variant="body2"><strong>Exactly 5 numbers ("12345")</strong>: Use <code>^\d{5}$</code></Typography>
                <Typography variant="body2">Valid: <code>12345</code>. Not valid: <code>1234</code>, <code>123456</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>Numbers only, any length ("12345")</strong>: Use <code>^\d+$</code></Typography>
                <Typography variant="body2">Valid: <code>1987</code>, <code>00234</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>Jira ticket ("ABC-123")</strong>: Use <code>^[A-Z]+-\d+$</code></Typography>
                <Typography variant="body2">Valid: <code>DEF-45</code>. Not valid: <code>def-45</code>, <code>DEF_45</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>ServiceNow ticket ("INC0012345")</strong>: Use <code>^INC\d+$</code></Typography>
                <Typography variant="body2">Valid: <code>INC123</code>. Not valid: <code>INC-123</code>, <code>INCabc</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>Letters and numbers mixed ("REQ45A")</strong>: Use <code>^[A-Za-z0-9]+$</code></Typography>
                <Typography variant="body2">Valid: <code>AB123C</code>. Not valid: <code>REQ 123</code>, <code>REQ-123</code></Typography>
              </li>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Tip:</strong> Test your regex patterns using online tools like regex101.com or regexr.com before implementing them in your project.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRegexHelpClose} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteConfirmDialog.open}
          onClose={closeDeleteConfirmDialog}
          maxWidth={false}
          PaperProps={{
            sx: {
              borderRadius: 2,
              width: 'auto',
              maxWidth: 'min(360px, calc(100vw - 32px))',
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, px: 2, pt: 1.75, fontSize: '1.05rem' }}>Delete project</DialogTitle>
          <DialogContent sx={{ px: 2, pt: 0, pb: 0.5, flex: '0 0 auto' }}>
            <Typography variant="body2">
              Are you sure you want to delete{' '}
              <strong>{deleteConfirmDialog.projectName}</strong>?
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 1.75, pt: 1.25, gap: 0.5 }}>
            <Button onClick={closeDeleteConfirmDialog} color="inherit" size="small" disabled={deletingProject}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteProject}
              variant="contained"
              color="error"
              size="small"
              disabled={deletingProject}
            >
              {deletingProject ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteErrorDialog.open}
          onClose={closeDeleteErrorDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ErrorOutlineIcon color="error" />
              <Typography variant="h6" component="span" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
                Unable to delete project
              </Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ borderRadius: 1.5 }}>
              {deleteErrorDialog.message}
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeDeleteErrorDialog} variant="contained" color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }